import React, { useMemo } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import HistoryIcon from '@mui/icons-material/History';
import Tooltip from '@mui/material/Tooltip';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import MarkerComponent from './Marker.component';
import CircularProgress from '@mui/material/CircularProgress';
import MapControlComponent from './Map-control.component';
import TrackComponent from './Track-component';
import useDictionary from '@ui/hooks/useDictionary';
import DotMarkerComponent from './Dot-marker.component';
import { IStoredDevice } from '@store/Devices.store';
import { getTrack, sortTrack } from './track.utils';
import { IProps } from './IProps';

type markerProps = Omit<
    IStoredDevice,
    'groupId' | 'type' | 'deviceType' | 'additionalInfo'
>;

const Component: React.FC<IProps> = ({
    loading,
    zoom,
    page,
    reload,
    coordsList,
    cookies,
    position,
    isSidebarHidden,
    timezone,
    isTelemetryVisible,
    highlightedDevice,
    highlightedCoords,
    highlightedDate,
    lang,
    trackMode,
    lastCoordsList,
    historyCoordsList,
    lastBounds,
    onMapDrag,
    onMapMove,
    onSideBarBtnClick,
    onReload,
    onMapZoom,
    onDeviceTelemetryOpen,
}: IProps) => {
    const getWord = useDictionary();
    const isHistory = page === 'history' || page === 'support';
    const isShortHeight = isHistory && isTelemetryVisible;
    const isTrack = trackMode && isHistory;
    const mapWrapClass = isShortHeight ? 'map__wrap' : 'map__wrap_fullheight';
    const mapOverlayClass = isShortHeight
        ? 'map__overlay'
        : 'map__overlay_fullheight';
    const sideBarTooltipText = isSidebarHidden
        ? getWord('showSideBar')
        : getWord('hideSideBar');
    const telemetryTooltipText = isTelemetryVisible
        ? getWord('hideTelemetry')
        : getWord('showTelemetry');

    const getMarkers = (
        {
            imei,
            name,
            icon,
            gpsCoord,
            lbsCoord,
            date,
            gpsDate,
            lbsDate,
            speed,
            dir,
            alt,
            sats,
            hdop,
            batteryVoltage,
            event,
            temperature,
            cur_sim,
            signal,
        }: markerProps,
        i: number,
    ) => {
        const props = {
            icon,
            gpsCoord,
            lbsCoord,
            imei,
            name,
            date,
            gpsDate,
            lbsDate,
            timezone,
            key: i,
            speed,
            dir,
            alt,
            sats,
            hdop,
            batteryVoltage,
            highlightedDevice,
            highlightedCoords,
            highlightedDate,
            event,
            temperature,
            cur_sim,
            signal,
            lang,
        };
        return <MarkerComponent {...props} />;
    };

    const getDotMarkers = (
        {
            imei,
            name,
            gpsCoord,
            lbsCoord,
            date,
            gpsDate,
            lbsDate,
            speed,
            dir,
            alt,
            sats,
            hdop,
            batteryVoltage,
            event,
            temperature,
            cur_sim,
            signal,
        }: markerProps,
        i: number,
    ) => {
        const props = {
            gpsCoord,
            lbsCoord,
            imei,
            name,
            date,
            gpsDate,
            lbsDate,
            timezone,
            key: i,
            speed,
            dir,
            alt,
            sats,
            hdop,
            batteryVoltage,
            highlightedDevice,
            highlightedCoords,
            highlightedDate,
            event,
            temperature,
            cur_sim,
            signal,
            lang,
        };
        return <DotMarkerComponent {...props} />;
    };
    const track = useMemo(() => {
        if (isHistory) return getTrack(historyCoordsList.sort(sortTrack));
        else return [];
    }, [coordsList]);
    const historyMarkers = useMemo(
        () => historyCoordsList.map(isTrack ? getDotMarkers : getMarkers),
        [
            historyCoordsList,
            timezone,
            highlightedCoords,
            highlightedDate,
            lang,
            isTrack,
        ],
    );
    const lastMarkers = useMemo(() => {
        return lastCoordsList.map(getMarkers);
    }, [
        lastCoordsList,
        page,
        historyCoordsList,
        timezone,
        highlightedDevice,
        lang,
    ]);

    return (
        <Box className={mapWrapClass} component="section">
            <MapContainer
                className="map"
                attributionControl={false}
                center={position}
                zoom={zoom}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapControlComponent
                    bounds={lastBounds}
                    position={position}
                    onMapDrag={onMapDrag}
                    reload={reload}
                    zoom={zoom}
                    onReload={onReload}
                    onMapZoom={onMapZoom}
                    onMapMove={onMapMove}
                />
                {isTrack && <TrackComponent coords={historyCoordsList} />}
                {!isTrack && isHistory && (
                    // @ts-ignore
                    <MarkerClusterGroup 
                    disableClusteringAtZoom={18}
                    removeOutsideVisibleBounds={true}
                    >{historyMarkers}</MarkerClusterGroup>
                )}
                {isTrack && isHistory && historyMarkers}
                {lastMarkers.length > 1 ? (
                    // @ts-ignore
                    <MarkerClusterGroup 
                    disableClusteringAtZoom={18}
                    removeOutsideVisibleBounds={true}
                    >{lastMarkers}</MarkerClusterGroup>
                ) : (
                    lastMarkers
                )}
            </MapContainer>
            <Box
                className={
                    cookies && !isTelemetryVisible
                        ? 'map__btn-bar_with-cookies'
                        : 'map__btn-bar'
                }
            >
                <Tooltip
                    classes={{
                        popper: 'map__tooltip-popper',
                        tooltip: 'map__tooltip',
                    }}
                    title={sideBarTooltipText}
                    placement="top"
                >
                    <Fab
                        className="map__btn_sidebar"
                        onClick={onSideBarBtnClick}
                    >
                        <ViewSidebarIcon className="map__ico" />
                    </Fab>
                </Tooltip>
                {isHistory && (
                    <Tooltip
                        classes={{
                            popper: 'map__tooltip-popper',
                            tooltip: 'map__tooltip',
                        }}
                        title={telemetryTooltipText}
                        placement="top"
                    >
                        <Fab
                            className="map__btn"
                            onClick={onDeviceTelemetryOpen}
                        >
                            <HistoryIcon />
                        </Fab>
                    </Tooltip>
                )}
            </Box>
            {loading && (
                <Box className={mapOverlayClass}>
                    <CircularProgress className="map__loader" size={70} />
                </Box>
            )}
        </Box>
    );
};

const MapComponent = React.memo(Component);

export default MapComponent;
